import * as React from "react";
import { createRoot, Root } from "react-dom/client";
import DOMPurify from "dompurify";
import { Text } from "@fluentui/react-components";
import {
  DataBarVerticalRegular,
  MoneyRegular,
  BoxRegular,
  MegaphoneRegular,
  LightbulbRegular,
  TargetArrowRegular,
  WarningRegular,
  ArrowTrendingRegular,
  ClipboardTextLtrRegular,
  TagRegular,
  PeopleRegular,
  ScalesRegular,
  StarRegular,
  SparkleRegular,
} from "@fluentui/react-icons";
import { SpreetailHeader } from "../layout/SpreetailHeader";

interface InsightsData {
  htmlContent?: string; // Raw HTML from backend
}

interface AIInsightsPanelProps {
  insightsData: InsightsData | null;
  selectedSKUs: string[];
  userPrompt: string | null;
  onGoBack: () => void;
  isFromHistory?: boolean;
}

// Fluent UI icon components mapped by semantic category
const ICON_COMPONENTS: Record<string, React.FC<{ style?: React.CSSProperties }>> = {
  chart: DataBarVerticalRegular,
  dollar: MoneyRegular,
  package: BoxRegular,
  megaphone: MegaphoneRegular,
  lightbulb: LightbulbRegular,
  target: TargetArrowRegular,
  alert: WarningRegular,
  trending: ArrowTrendingRegular,
  clipboard: ClipboardTextLtrRegular,
  tag: TagRegular,
  users: PeopleRegular,
  scale: ScalesRegular,
  star: StarRegular,
  sparkle: SparkleRegular,
};

// Keyword-to-icon category mapping — broad enough to catch any LLM heading
const ICON_RULES: [string[], string][] = [
  [["sales", "revenue", "profit", "pricing", "price", "cost", "spend", "margin", "acos", "roas", "roi", "budget", "financial", "earnings", "income"], "dollar"],
  [["inventory", "stock", "fulfillment", "order", "supply", "warehouse", "shipping", "logistics", "lead time", "stockout", "dead stock", "replenish"], "package"],
  [["marketing", "campaign", "advertising", "ad ", "ads ", "promotion", "awareness", "impression", "click", "ctr", "cpc", "asin", "keyword", "search term", "sponsored"], "megaphone"],
  [["risk", "warning", "critical", "concern", "issue", "problem", "decline", "drop", "loss", "threat", "alert", "attention", "negative", "underperform"], "alert"],
  [["growth", "velocity", "trend", "increase", "improvement", "momentum", "acceleration", "surge", "uptick", "positive", "gain", "upward"], "trending"],
  [["analysis", "insight", "finding", "observation", "pattern", "diagnostic", "assessment", "evaluation", "breakdown", "deep dive", "drill"], "lightbulb"],
  [["recommendation", "recommend", "action", "next step", "strategy", "suggestion", "optimization", "initiative", "tactic", "priority", "plan"], "target"],
  [["performance", "metric", "dashboard", "kpi", "scorecard", "benchmark", "efficiency", "conversion", "rate", "ratio", "index"], "chart"],
  [["overview", "executive", "summary", "report", "recap", "snapshot", "highlight", "takeaway", "conclusion", "key point"], "clipboard"],
  [["comparison", "compare", "versus", "vs", "period", "month-over", "year-over", "yoy", "mom", "quarter", "week-over", "change", "delta"], "scale"],
  [["brand", "product", "category", "segment", "classification", "group", "type", "class", "tier", "cluster", "sku"], "tag"],
  [["audience", "customer", "demographic", "user", "buyer", "shopper", "consumer", "target audience", "persona"], "users"],
  [["opportunity", "gap", "potential", "market gap", "white space", "untapped", "upside"], "star"],
];

// Resolve heading text to an icon category key
const getIconKey = (sectionTitle: string): string => {
  const title = sectionTitle.toLowerCase().trim();
  for (const [keywords, iconKey] of ICON_RULES) {
    if (keywords.some((kw) => title.includes(kw))) {
      return iconKey;
    }
  }
  return "sparkle";
};

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  insightsData,
  selectedSKUs: _selectedSKUs,
  userPrompt: _userPrompt,
  onGoBack,
  isFromHistory = false,
}) => {
  const contentRef = React.useRef<HTMLDivElement>(null);
  const iconRootsRef = React.useRef<Root[]>([]);

  // Sanitize HTML content (no icon injection — icons mounted via useEffect)
  const processedHtmlContent = React.useMemo(() => {
    if (!insightsData?.htmlContent) return null;
    return DOMPurify.sanitize(insightsData.htmlContent, {
      ADD_TAGS: ["img"],
      ADD_ATTR: ["class", "style", "src", "alt"],
    });
  }, [insightsData?.htmlContent]);

  // Mount Fluent UI icons into each <h3> after HTML renders
  React.useEffect(() => {
    // Cleanup previous icon roots
    iconRootsRef.current.forEach((root) => root.unmount());
    iconRootsRef.current = [];

    if (contentRef.current && processedHtmlContent) {
      const headings = contentRef.current.querySelectorAll("h3");
      headings.forEach((h3) => {
        // Skip if icon already mounted
        if (h3.querySelector(".section-icon")) return;

        const titleText = h3.textContent || "";
        const iconKey = getIconKey(titleText);
        const IconComponent = ICON_COMPONENTS[iconKey] || ICON_COMPONENTS.sparkle;

        // Create a container span and prepend it to the h3
        const iconContainer = document.createElement("span");
        iconContainer.className = "section-icon";
        h3.prepend(iconContainer);

        // Mount the React icon component into the container
        const root = createRoot(iconContainer);
        root.render(
          React.createElement(IconComponent, {
            style: { fontSize: 18, color: "#068E75" },
          })
        );
        iconRootsRef.current.push(root);
      });
    }

    // Cleanup on unmount or re-render
    return () => {
      iconRootsRef.current.forEach((root) => root.unmount());
      iconRootsRef.current = [];
    };
  }, [processedHtmlContent]);

  return (
    <>
      <style>
        {`
          .bg-gradient-custom {
            background: linear-gradient(180deg, #D3E8D9 0%, #EEEEEE 79.45%);
          }
          
          /* Styling for backend HTML content */
          .ai-insights-html-content {
            font-family: inherit;
          }

          .ai-insights-html-content .insights-section {
            margin-bottom: 1.5rem;
            border-radius: 14px;
            backdrop-filter: blur(10px);
            background: linear-gradient(180deg, rgba(255,255,255,0.75), rgba(255,255,255,0.60));
            border: 1px solid rgba(6,142,117,0.10);
            box-shadow: 0 4px 12px rgba(0,0,0,0.06);
            overflow: hidden;
          }

          .ai-insights-html-content .insights-section h3 {
            font-size: 0.9375rem;
            font-weight: 600;
            color: #111827;
            margin: 0;
            padding: 0.875rem 1rem;
            border-bottom: 1px solid rgba(6,142,117,0.10);
            border-left: 3px solid #068E75;
            display: flex;
            align-items: center;
            background: rgba(6,142,117,0.04);
          }

          .ai-insights-html-content .insights-section h3 .section-icon {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 20px;
            height: 20px;
            margin-right: 0.75rem;
            flex-shrink: 0;
          }

          .ai-insights-html-content .insights-section p {
            font-size: 0.8125rem;
            color: #374151;
            line-height: 1.6;
            margin: 0;
            padding: 0.75rem 1rem;
          }

          .ai-insights-html-content .insights-section p strong {
            font-weight: 600;
            color: #111827;
          }

          .ai-insights-html-content .insights-list {
            list-style: none;
            padding: 0.75rem;
            margin: 0;
          }

          .ai-insights-html-content .insights-list li {
            padding: 0.625rem 0.75rem;
            margin-bottom: 0.5rem;
            border-radius: 8px;
            background: rgba(255, 255, 255, 0.55);
            border: 1px solid rgba(6,142,117,0.08);
            box-shadow: 0 1px 4px rgba(0,0,0,0.03);
            transition: background 0.15s ease;
          }

          .ai-insights-html-content .insights-list li:hover {
            background: rgba(255, 255, 255, 0.75);
          }

          .ai-insights-html-content .insights-list li:last-child {
            margin-bottom: 0;
          }

          .ai-insights-html-content .item-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 0.75rem;
          }

          .ai-insights-html-content .item-text {
            flex: 1;
            font-size: 0.8125rem;
            color: #374151;
            line-height: 1.55;
          }

          .ai-insights-html-content .item-text strong {
            font-weight: 600;
            color: #111827;
          }

          .ai-insights-html-content .metric-badge {
            display: inline-block;
            padding: 0.2rem 0.625rem;
            border-radius: 9999px;
            font-size: 0.6875rem;
            font-weight: 500;
            white-space: nowrap;
            letter-spacing: 0.01em;
          }

          .ai-insights-html-content .metric-badge.critical {
            background-color: #FEE2E2;
            color: #991B1B;
            border: 1px solid #FCA5A5;
          }

          .ai-insights-html-content .metric-badge.warning {
            background-color: #FEF9C3;
            color: #854D0E;
            border: 1px solid #FDE68A;
          }

          .ai-insights-html-content .metric-badge.info {
            background-color: #DBEAFE;
            color: #1E40AF;
            border: 1px solid #BFDBFE;
          }

          .ai-insights-html-content .metric-badge.good {
            background-color: #D1FAE5;
            color: #065F46;
            border: 1px solid #6EE7B7;
          }

          .ai-insights-html-content .metrics-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
            gap: 0.75rem;
            padding: 0.75rem;
          }

          .ai-insights-html-content .metric-card {
            padding: 0.875rem;
            border-radius: 10px;
            background: rgba(255,255,255,0.65);
            border: 1px solid rgba(6,142,117,0.10);
            box-shadow: 0 2px 8px rgba(0,0,0,0.04);
            transition: box-shadow 0.15s ease, transform 0.15s ease;
          }

          .ai-insights-html-content .metric-card:hover {
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            transform: translateY(-1px);
          }

          .ai-insights-html-content .metric-value {
            font-size: 1.375rem;
            font-weight: 700;
            color: #068E75;
            margin-bottom: 0.25rem;
          }

          .ai-insights-html-content .metric-label {
            font-size: 0.8125rem;
            color: #6B7280;
          }

          /* Inline code styling for LLM output */
          .ai-insights-html-content code {
            font-size: 0.8125rem;
            background: rgba(6,142,117,0.08);
            color: #065F46;
            padding: 0.125rem 0.375rem;
            border-radius: 4px;
            font-family: 'SF Mono', 'Fira Code', monospace;
          }

          /* Table styling for LLM-generated tables */
          .ai-insights-html-content table {
            width: 100%;
            border-collapse: collapse;
            margin: 0.75rem 0;
            font-size: 0.8125rem;
          }

          .ai-insights-html-content th {
            background: rgba(6,142,117,0.08);
            color: #065F46;
            font-weight: 600;
            text-align: left;
            padding: 0.5rem 0.75rem;
            border-bottom: 2px solid rgba(6,142,117,0.15);
          }

          .ai-insights-html-content td {
            padding: 0.5rem 0.75rem;
            border-bottom: 1px solid rgba(0,0,0,0.06);
            color: #374151;
          }

          .ai-insights-html-content tr:last-child td {
            border-bottom: none;
          }

          .ai-insights-html-content tr:hover td {
            background: rgba(6,142,117,0.03);
          }

          /* Hide scrollbar while keeping scroll functionality */
          .ai-insights-scroll::-webkit-scrollbar {
            display: none;
          }
          .ai-insights-scroll {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}
      </style>
      <div className="h-screen flex flex-col relative font-sans text-gray-800 bg-gradient-custom">
        <div
          className="absolute -right-10 -top-10 w-50 h-50 blur-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(134, 239, 172, 0.3), transparent 70%)",
          }}
        ></div>
        <div
          className="absolute -left-8 -bottom-8 w-38 h-38 blur-[40px] pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at center, rgba(167, 243, 208, 0.3), transparent 70%)",
          }}
        ></div>

        <SpreetailHeader />

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-5 pb-5 min-h-0 ai-insights-scroll">
          <div className="max-w-4xl mx-auto pt-5">
            {/* Header Section */}
            <div className="mb-6">
              <div className={`mb-2 flex items-center ${isFromHistory ? "justify-between" : "justify-start"} gap-3`}>
                <Text
                  size={600}
                  weight="semibold"
                  className="text-gray-900"
                  as="h1"
                  style={{ fontSize: "24px", lineHeight: "1.2" }}
                >
                  AI Insight dashboard
                </Text>

                {isFromHistory && (
                  <button
                    type="button"
                    onClick={onGoBack}
                    className="inline-flex items-center gap-1.5 text-xs font-medium cursor-pointer"
                    style={{
                      backgroundColor: "rgba(16,185,129,0.10)",
                      border: "1.5px solid rgba(16,185,129,0.35)",
                      borderRadius: "9999px",
                      color: "#065F46",
                      padding: "4px 10px",
                      lineHeight: 1.2,
                      fontFamily: "inherit",
                      whiteSpace: "nowrap",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.16)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "rgba(16,185,129,0.10)";
                    }}
                  >
                    <span className="inline-block text-sm leading-none" aria-hidden="true">
                      ←
                    </span>
                    <span>Back</span>
                  </button>
                )}
              </div>

              <div>
                <Text
                  as="p"
                  size={300}
                  className="text-gray-500"
                  style={{ marginTop: 0, marginBottom: 0, fontSize: "14px" }}
                >
                  Strategic insights and recommendations based on your data
                </Text>
              </div>
            </div>

            {/* HTML Content from Backend */}
            {processedHtmlContent && (
              <div
                ref={contentRef}
                className="ai-insights-html-content"
                dangerouslySetInnerHTML={{ __html: processedHtmlContent }}
              />
            )}

            {/* Empty State */}
            {!insightsData?.htmlContent && (
              <div className="flex flex-col items-center justify-center py-16">
                <div style={{ marginBottom: "28px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <img
                    src="/assets/Search File.png"
                    alt="No insights"
                    style={{
                      width: "80px",
                      height: "80px",
                      objectFit: "contain",
                      opacity: 1,
                    }}
                  />
                </div>
                <div className="text-center" style={{ marginBottom: "32px" }}>
                  <h2
                    style={{
                      fontSize: "18px",
                      fontWeight: 600,
                      lineHeight: "1.4",
                      color: "#1F2937",
                      marginBottom: "10px",
                      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      letterSpacing: "-0.01em",
                      marginTop: 0,
                    }}
                  >
                    No Insights Available
                  </h2>
                  <p
                    style={{
                      fontSize: "13px",
                      fontWeight: 400,
                      lineHeight: "1.5",
                      color: "#6B7280",
                      fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                      margin: 0,
                      padding: 0,
                    }}
                  >
                    We couldn't generate insights with the<br />current data. Try adjusting your filters or check back later.
                  </p>
                </div>
                <button
                  onClick={onGoBack}
                  className="text-white border-none rounded-md p-3 px-4 text-[13px] font-semibold transition-all duration-200 shadow-md flex items-center justify-center"
                  style={{
                    backgroundColor: "#068E75",
                    boxShadow: "0 2px 4px rgba(6, 142, 117, 0.3)",
                    cursor: "pointer",
                    width: "100%",
                    maxWidth: "380px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "#057A63";
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow = "0 4px 8px rgba(6, 142, 117, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "#068E75";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 2px 4px rgba(6, 142, 117, 0.3)";
                  }}
                >
                  Go Back
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AIInsightsPanel;